import * as React from 'react';
import MainLayout from '../../components/Layout/MainLayout.tsx';
import RangeDatePicker from '../../components/Layout/DatePicker.tsx';
import 'react-datepicker/dist/react-datepicker.css';
import '../../components/Layout/DatePicker.css';
import { CopyIcon, HashIcon, CheckIcon } from '../../components/icons/Icons.tsx';
import { Send, Download, Filter, RefreshCw, CheckCircle, ArrowRight, AlertTriangle, Clock, DollarSign, Users, FileText, Shield, X, Upload, Calendar, ArrowLeft, TrendingUp, TrendingDown, Target, Briefcase, Globe, Activity, BarChart2, Percent } from 'react-feather';

// --- Reused from BNPLWalletPage ---

// Mock Data Structure for Credit transactions
interface Transaction {
  id: string;
  transactionDate: string;
  businessPartner: string; // Business partner instead of consumer partner
  product: 'Credit'; // Specific to this page
  type: string; // Loan disbursement, repayment, interest payment, etc.
  amount: number;
  currency: string;
  status: 'Completed' | 'Pending' | 'Failed' | 'Scheduled';
  location?: string;
  logoUrl?: string | null;
  category?: 'Credit' | 'Debit' | 'Correction';
  reference?: string;
  description?: string;
  loanDetails?: {
    interestRate: number;
    term: number; // In months
    outstandingBalance: number;
  };
  approvedBy?: string;
}

// Partner data interface (Reused)
interface BusinessPartner {
  name: string;
  location: string;
  logoUrl?: string | null;
  creditScore?: number;
  relationship?: string;
}

// DropdownFilterProps interface (Reused)
interface DropdownFilterProps {
    label: string;
    options: string[];
    selectedValue: string;
    onChange: (value: string) => void;
}

// DropdownFilter Component (Reused)
const DropdownFilter: React.FC<DropdownFilterProps> = ({ label, options, selectedValue, onChange }) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [hoveredOption, setHoveredOption] = React.useState<string | null>(null);
    const dropdownRef = React.useRef<HTMLDivElement>(null);

    const handleSelect = (option: string) => {
        onChange(option);
        setIsOpen(false);
    };

    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    return (
        <div style={{ position: 'relative' }} ref={dropdownRef}>
            <button
                style={{
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    backgroundColor: 'white',
                    fontSize: '14px',
                    color: '#374151',
                    cursor: 'pointer',
                    minWidth: '140px',
                    textAlign: 'left',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
                }}
                onClick={() => setIsOpen(!isOpen)}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = '#adb5bd'}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
            >
                {label}: <span style={{ fontWeight: '500', color: '#1f2937' }}>{selectedValue}</span>
                <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="#6b7280" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d={isOpen ? "m18 14-6-6-6 6" : "m6 10 6 6 6-6"} />
                </svg>
            </button>
            {isOpen && (
                <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)', zIndex: 10, minWidth: '160px', padding: '4px 0' }}>
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0, maxHeight: '200px', overflowY: 'auto' }}>
                        {options.map(option => (
                            <li
                                key={option}
                                style={{
                                    padding: '8px 16px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    color: '#374151',
                                    backgroundColor: hoveredOption === option ? '#f9fafb' : 'white'
                                }}
                                onClick={() => handleSelect(option)}
                                onMouseEnter={() => setHoveredOption(option)}
                                onMouseLeave={() => setHoveredOption(null)}
                            >
                                {option}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

// Business Partner Location Mapping
const partnerLocations: { [key: string]: string } = {
  'ACME Corp': 'UAE', 'Global Trade LLC': 'KSA', 'Desert Ventures': 'UAE', 'Gulf Traders': 'KSA',
  'Al Falah Enterprises': 'UAE', 'Riyadh Commerce': 'KSA', 'Future Tech': 'UAE', 'Doha Trading': 'Qatar',
  'Kuwait Business Group': 'Kuwait', 'Jarir Holding': 'Kuwait', 'Manama Imports': 'Bahrain',
  'LuLu Group': 'Bahrain', 'Qatar Ventures': 'Qatar',
  'Al Othaim Group': 'KSA', 'Dubai Holdings': 'UAE',
};

// Partner Logo Mapping
const partnerLogos: { [key: string]: string | null } = {
  'ACME Corp': null,
  'Global Trade LLC': null,
  'Desert Ventures': null,
  'Gulf Traders': null,
  'Al Falah Enterprises': null,
  'Riyadh Commerce': null,
  'Future Tech': null,
  'Doha Trading': null,
  'Kuwait Business Group': null,
  'Jarir Holding': 'https://upload.wikimedia.org/wikipedia/en/5/58/Jarir_Bookstore_logo.svg',
  'Manama Imports': null,
  'LuLu Group': null,
  'Qatar Ventures': null,
  'Al Othaim Group': null,
  'Dubai Holdings': null,
};

// Function to generate random ISO date string
const getRandomISODate = (baseDate: string, index: number): string => {
  const date = new Date(baseDate);
  const hour = Math.floor(Math.random() * 24);
  const minute = Math.floor(Math.random() * 60);
  const second = Math.floor(Math.random() * 60);
  date.setUTCHours(hour, minute, second, 0);
  return date.toISOString();
};

// Function to generate a simple UUID v4
const generateUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// Helper function to determine transaction category
const getTransactionCategoryBase = (transaction: Pick<Transaction, 'type' | 'amount'>): 'Credit' | 'Debit' | 'Correction' => {
    if (transaction.type.toLowerCase().includes('correction')) return 'Correction';
    if (transaction.amount >= 0) return 'Credit';
    return 'Debit';
};

// Base Mock Transactions for Credit product
const rawMockCreditTransactions: Omit<Transaction, 'id' | 'location' | 'logoUrl' | 'product'>[] = [
  { transactionDate: '2025-04-21', businessPartner: 'ACME Corp', type: 'Loan Disbursement', amount: 250000.00, currency: 'AED', status: 'Completed' },
  { transactionDate: '2025-04-22', businessPartner: 'ACME Corp', type: 'Interest Payment', amount: 3750.00, currency: 'AED', status: 'Completed' },
  { transactionDate: '2025-04-23', businessPartner: 'ACME Corp', type: 'Principal Repayment', amount: 20833.33, currency: 'AED', status: 'Completed' },
  { transactionDate: '2025-04-24', businessPartner: 'ACME Corp', type: 'Fee Adjustment', amount: -500.00, currency: 'AED', status: 'Completed' },
  { transactionDate: '2025-04-21', businessPartner: 'Al Falah Enterprises', type: 'Loan Disbursement', amount: 180000.00, currency: 'AED', status: 'Completed' },
  { transactionDate: '2025-04-22', businessPartner: 'Al Falah Enterprises', type: 'Interest Payment', amount: 2700.00, currency: 'AED', status: 'Completed' },
  { transactionDate: '2025-04-23', businessPartner: 'Gulf Traders', type: 'Loan Disbursement', amount: 120000.00, currency: 'SAR', status: 'Completed' },
  { transactionDate: '2025-04-24', businessPartner: 'Gulf Traders', type: 'Late Payment Fee', amount: 1200.00, currency: 'SAR', status: 'Completed' },
  { transactionDate: '2025-04-21', businessPartner: 'Doha Trading', type: 'Loan Disbursement', amount: 200000.00, currency: 'QAR', status: 'Completed' },
  { transactionDate: '2025-04-22', businessPartner: 'Doha Trading', type: 'Principal Repayment', amount: 16666.67, currency: 'QAR', status: 'Pending' },
  { transactionDate: '2025-04-21', businessPartner: 'Kuwait Business Group', type: 'Loan Disbursement', amount: 90000.00, currency: 'KWD', status: 'Completed' },
  { transactionDate: '2025-04-22', businessPartner: 'Kuwait Business Group', type: 'Interest Payment', amount: 1350.00, currency: 'KWD', status: 'Completed' },
  { transactionDate: '2025-04-23', businessPartner: 'Kuwait Business Group', type: 'Early Repayment', amount: 45000.00, currency: 'KWD', status: 'Completed' },
  { transactionDate: '2025-04-24', businessPartner: 'Kuwait Business Group', type: 'Early Repayment Fee', amount: -900.00, currency: 'KWD', status: 'Completed' },
  { transactionDate: '2025-04-21', businessPartner: 'LuLu Group', type: 'Loan Disbursement', amount: 250000.00, currency: 'BHD', status: 'Completed' },
  { transactionDate: '2025-04-22', businessPartner: 'LuLu Group', type: 'Interest Payment', amount: 3750.00, currency: 'BHD', status: 'Completed' },
  { transactionDate: '2025-04-23', businessPartner: 'LuLu Group', type: 'Principal Repayment', amount: 20833.33, currency: 'BHD', status: 'Pending' },
  { transactionDate: '2025-04-24', businessPartner: 'LuLu Group', type: 'Late Payment Fee', amount: 1250.00, currency: 'BHD', status: 'Scheduled' },
  { transactionDate: '2025-04-27', businessPartner: 'Dubai Holdings', type: 'Loan Disbursement', amount: 500000.00, currency: 'AED', status: 'Pending' },
  { transactionDate: '2025-04-28', businessPartner: 'Dubai Holdings', type: 'Admin Fee', amount: -2500.00, currency: 'AED', status: 'Completed' },
];

// Add loan details to transactions
const rawMockCreditTransactionsWithLoanDetails = rawMockCreditTransactions.map(tx => {
  // Only add loan details to loan disbursements
  if (tx.type === 'Loan Disbursement') {
    return {
      ...tx,
      loanDetails: {
        interestRate: 6 + Math.random() * 4, // 6-10% interest rate
        term: [6, 12, 24, 36][Math.floor(Math.random() * 4)], // 6, 12, 24, or 36 month terms
        outstandingBalance: tx.amount // Initially, outstanding balance equals loan amount
      }
    };
  }
  return tx;
});

// Process raw transactions specifically for Credit
const processedCreditTransactions: Transaction[] = rawMockCreditTransactionsWithLoanDetails
  .map((tx, index) => ({
    ...tx,
    id: generateUUID(),
    product: 'Credit', // Assign Credit product
    transactionDate: getRandomISODate(tx.transactionDate, index),
    location: partnerLocations[tx.businessPartner] || 'N/A',
    logoUrl: partnerLogos[tx.businessPartner],
    category: getTransactionCategoryBase(tx as any), // Derive category
    partner: tx.businessPartner, // Maintain compatibility with transaction interface
  } as unknown as Transaction)) // Cast to Transaction
  .sort((a, b) => new Date(b.transactionDate).getTime() - new Date(a.transactionDate).getTime());

// Helper function for currency formatting
const formatCurrency = (amount: number, currency: string): string => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return formatter.format(amount);
};

// Helper to get partner initials
const getInitials = (name: string): string => {
  if (!name) return '??';
  const words = name.split(' ').filter(Boolean);
  if (words.length === 0) return '??';
  if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
  const firstInitial = words[0][0];
  const lastInitial = words[words.length - 1][0];
  return (firstInitial + lastInitial).toUpperCase();
};

// Helper function to format date and time
const formatDateTime = (isoString: string): { date: string, time: string } => {
  try {
    const dateObj = new Date(isoString);
    const formattedDate = dateObj.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
    const formattedTime = dateObj.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();
    return { date: formattedDate, time: formattedTime };
  } catch (error) {
    console.error("Error formatting date:", isoString, error);
    return { date: 'Invalid Date', time: '' };
  }
};

// Status Badge Component
const StatusBadge: React.FC<{ status: Transaction['status'] }> = ({ status }) => {
  let styles: React.CSSProperties = {
    padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 500,
    textTransform: 'capitalize', display: 'inline-block',
  };
  switch (status) {
    case 'Completed': styles = { ...styles, backgroundColor: '#ecfdf5', color: '#059669' }; break;
    case 'Pending': styles = { ...styles, backgroundColor: '#fefce8', color: '#d97706' }; break;
    case 'Failed': styles = { ...styles, backgroundColor: '#fef2f2', color: '#dc2626' }; break;
    case 'Scheduled': styles = { ...styles, backgroundColor: '#eff6ff', color: '#3b82f6' }; break;
    default: styles = { ...styles, backgroundColor: '#f8fafc', color: '#64748b' }; break;
  }
  return <span style={styles}>{status}</span>;
};

// Category Badge Component
const CategoryBadge: React.FC<{ category: 'Credit' | 'Debit' | 'Correction' }> = ({ category }) => {
    let styles: React.CSSProperties = {
      borderRadius: '12px', fontSize: '12px', fontWeight: 500,
      textTransform: 'capitalize', display: 'inline-block',
    };
    switch (category) {
      case 'Credit': styles = { ...styles, color: '#059669' }; break;
      case 'Debit': styles = { ...styles, color: '#ef4444' }; break;
      case 'Correction': styles = { ...styles, color: '#4f46e5' }; break;
      default: styles = { ...styles, color: '#64748b' }; break;
    }
    return <span style={styles}>{category}</span>;
};

// Transaction Details Panel Component
interface TransactionDetailsPanelProps {
  transaction: Transaction | null;
  onClose: () => void;
  isVisible: boolean;
}

const TransactionDetailsPanel: React.FC<TransactionDetailsPanelProps> = ({ transaction, onClose, isVisible }) => {
  const panelRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if ((event.target as Element).closest('[data-transaction-row="true"]')) return; // Skip if clicking a table row
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) onClose();
    };
    if (isVisible) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isVisible, onClose]);

  if (!transaction) return null;

  const dateTime = formatDateTime(transaction.transactionDate);
  const category = getTransactionCategoryBase(transaction);

  return (
    <div
      ref={panelRef}
      style={{
        position: 'fixed', top: '64px', right: 0, width: '380px', height: 'calc(100vh - 64px)',
        backgroundColor: 'white', boxShadow: '-4px 0 10px rgba(0, 0, 0, 0.1)', zIndex: 1000,
        overflowY: 'auto', transform: isVisible ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.2s ease-out', padding: '24px',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: '#111827' }}>Transaction Details</h2>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background-color 0.2s' }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <X size={20} />
        </button>
      </div>

      {/* Content */}
      <div>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
          <StatusBadge status={transaction.status} />
          {category === 'Correction' && <span style={{ backgroundColor: '#eef2ff', color: '#4f46e5', padding: '6px 12px', borderRadius: '16px', fontSize: '14px', fontWeight: 500 }}>Correction</span>}
        </div>

        <div style={{ marginBottom: '32px' }}>
          <p style={{ color: '#6b7280', fontSize: '16px', margin: '0 0 8px 0', fontWeight: 500 }}>Amount</p>
          <h3 style={{ fontSize: '32px', fontWeight: 600, margin: 0, color: transaction.amount >= 0 ? '#047857' : '#dc2626' }}>
            {transaction.amount >= 0 ? '+' : ''}{formatCurrency(transaction.amount, transaction.currency)}
          </h3>
        </div>

        <div style={{ height: '1px', backgroundColor: '#e5e7eb', margin: '24px 0' }}></div>

        <div style={{ marginBottom: '16px' }}>
          <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0' }}>Business Partner</p>
          <div style={{ color: '#111827', fontSize: '15px', fontWeight: 500 }}>{transaction.businessPartner}</div>
        </div>
        <div style={{ marginBottom: '16px' }}>
          <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0' }}>Date & Time</p>
          <div style={{ color: '#111827', fontSize: '15px', fontWeight: 500 }}>{dateTime.date}, {dateTime.time}</div>
        </div>
        <div style={{ marginBottom: '16px' }}>
          <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0' }}>Type</p>
          <div style={{ color: '#111827', fontSize: '15px', fontWeight: 500 }}>{transaction.type}</div>
        </div>
        
        {/* Loan details section - only shown for loan disbursements */}
        {transaction.loanDetails && (
          <>
            <div style={{ height: '1px', backgroundColor: '#e5e7eb', margin: '24px 0' }}></div>
            <div style={{ marginBottom: '16px' }}>
              <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 12px 0', fontWeight: 500 }}>Loan Details</p>
              
              <div style={{ marginBottom: '12px' }}>
                <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0' }}>Interest Rate</p>
                <div style={{ color: '#111827', fontSize: '15px', fontWeight: 500 }}>
                  {transaction.loanDetails.interestRate.toFixed(2)}%
                </div>
              </div>
              
              <div style={{ marginBottom: '12px' }}>
                <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0' }}>Term</p>
                <div style={{ color: '#111827', fontSize: '15px', fontWeight: 500 }}>
                  {transaction.loanDetails.term} months
                </div>
              </div>
              
              <div>
                <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0' }}>Outstanding Balance</p>
                <div style={{ color: '#111827', fontSize: '15px', fontWeight: 500 }}>
                  {formatCurrency(transaction.loanDetails.outstandingBalance, transaction.currency)}
                </div>
              </div>
            </div>
          </>
        )}
        
        <div style={{ display: 'flex', alignItems: 'center', marginTop: '40px', color: '#9ca3af', fontSize: '14px' }}>
          <Clock size={16} style={{ marginRight: '6px' }} />
          Transaction ID: {transaction.id.substring(0, 8)}
        </div>
      </div>
    </div>
  );
};

// Widget Component for Country Balance
interface CountryBalanceWidgetProps {
  country: string;
  balance: number;
  currency: string;
  change: number;
  trend: 'up' | 'down' | 'neutral';
}

const CountryBalanceWidget: React.FC<CountryBalanceWidgetProps> = ({ country, balance, currency, change, trend }) => {
  return (
    <div style={{ 
      backgroundColor: 'white', 
      borderRadius: '12px', 
      padding: '20px', 
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
      border: '1px solid #f3f4f6',
      display: 'flex',
      flexDirection: 'column',
      height: '100%'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
        <div style={{ 
          width: '36px', 
          height: '36px', 
          borderRadius: '50%', 
          backgroundColor: '#f0fdf4', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          marginRight: '12px'
        }}>
          <Globe size={18} color="#10b981" />
        </div>
        <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#111827', margin: 0 }}>{country}</h3>
      </div>
      
      <div style={{ marginBottom: '8px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: 700, margin: '0', color: '#111827' }}>
          {formatCurrency(balance, currency)}
        </h2>
      </div>
      
      <div style={{ 
        display: 'flex', 
        alignItems: 'center',
        fontSize: '14px', 
        fontWeight: 500,
        color: trend === 'up' ? '#10b981' : trend === 'down' ? '#ef4444' : '#6b7280'
      }}>
        {trend === 'up' ? <TrendingUp size={16} style={{ marginRight: '4px' }} /> : 
         trend === 'down' ? <TrendingDown size={16} style={{ marginRight: '4px' }} /> : null}
        {change > 0 ? '+' : ''}{formatCurrency(change, currency)}
      </div>
    </div>
  );
};

// Metric Widget Component
interface MetricWidgetProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  secondaryValue?: string;
  secondaryColor?: string;
  bgColor?: string;
  iconColor?: string;
}

const MetricWidget: React.FC<MetricWidgetProps> = ({ 
  title, 
  value, 
  icon,
  secondaryValue, 
  secondaryColor = '#10b981',
  bgColor = '#f0fdf4',
  iconColor = '#10b981'
}) => {
  // Render the icon directly, applying size and color
  const renderIcon = () => {
    if (React.isValidElement(icon)) {
      try {
        return React.cloneElement(icon as React.ReactElement<{ size?: number; color?: string }>, { size: 18, color: iconColor });
      } catch (e) {
        console.error("Could not clone icon element:", e);
        return icon;
      }
    } else if (typeof icon === 'function') {
       const IconComponent = icon as React.ElementType;
       return <IconComponent size={18} color={iconColor} />;
    }
    return null;
  };

  return (
    <div style={{ 
      backgroundColor: 'white', 
      borderRadius: '12px', 
      padding: '20px', 
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
      border: '1px solid #f3f4f6',
      display: 'flex',
      flexDirection: 'column',
      height: '100%'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
        <div style={{ 
          width: '36px', 
          height: '36px', 
          borderRadius: '50%', 
          backgroundColor: bgColor, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          marginRight: '12px'
        }}>
          {renderIcon()}
        </div>
        <h3 style={{ fontSize: '14px', fontWeight: 500, color: '#6b7280', margin: 0 }}>{title}</h3>
      </div>
      
      <div style={{ marginBottom: secondaryValue ? '8px' : 0 }}>
        <h2 style={{ fontSize: '24px', fontWeight: 700, margin: '0', color: '#111827' }}>
          {value}
        </h2>
      </div>
      
      {secondaryValue && (
        <div style={{ fontSize: '14px', fontWeight: 500, color: secondaryColor }}>
          {secondaryValue}
        </div>
      )}
    </div>
  );
};

// Main Balance Widget Component
interface MainBalanceWidgetProps {
  title: string;
  totalBalance: number;
  change: number;
  currency: string;
  icon?: React.ReactNode;
}

const MainBalanceWidget: React.FC<MainBalanceWidgetProps> = ({ 
  title = "Total Balance",
  totalBalance, 
  change, 
  currency, 
  icon = <DollarSign />
}) => {
  const trend = change >= 0 ? 'up' : 'down';
  const changeText = `${change >= 0 ? '+' : ''}${formatCurrency(change, currency)}`;
  const changeColor = trend === 'up' ? '#10b981' : '#ef4444';
  const defaultBgColor = '#f0fdf4';
  const defaultIconColor = '#10b981';

  const renderIcon = () => {
    if (React.isValidElement(icon)) {
      try {
        return React.cloneElement(icon as React.ReactElement<{ size?: number; color?: string }>, { size: 18, color: defaultIconColor });
      } catch (e) {
        console.error("Could not clone icon element:", e);
        return icon; 
      }
    } else if (typeof icon === 'function') {
       const IconComponent = icon as React.ElementType;
       return <IconComponent size={18} color={defaultIconColor} />;
    }
    return null; 
  };

  return (
    <div style={{ 
      backgroundColor: 'white', 
      borderRadius: '12px', 
      padding: '20px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
      border: '1px solid #f3f4f6',
      display: 'flex',
      flexDirection: 'column',
      height: '100%'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
        <div style={{ 
          width: '36px', 
          height: '36px', 
          borderRadius: '50%', 
          backgroundColor: defaultBgColor, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          marginRight: '12px'
        }}>
          {renderIcon()}
        </div>
        <h3 style={{ fontSize: '14px', fontWeight: 500, color: '#6b7280', margin: 0 }}>{title}</h3>
      </div>
      
      <div style={{ marginBottom: '8px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: 700, margin: '0', color: '#111827' }}>
          {formatCurrency(totalBalance, currency)}
        </h2>
      </div>
      
      <div style={{ 
        display: 'flex', 
        alignItems: 'center',
        fontSize: '14px', 
        fontWeight: 500,
        color: changeColor
      }}>
        {trend === 'up' ? <TrendingUp size={16} style={{ marginRight: '4px' }} /> : 
         <TrendingDown size={16} style={{ marginRight: '4px' }} />}
        {changeText}
      </div>
    </div>
  );
};

// --- Credit Wallet Page Component ---

const CreditWalletPage: React.FC = () => {
  const [transactions, setTransactions] = React.useState<Transaction[]>(processedCreditTransactions);
  const [hoveredRowIndex, setHoveredRowIndex] = React.useState<number | null>(null);
  const [searchTerm, setSearchTerm] = React.useState<string>('');
  const [hoveredIdIcon, setHoveredIdIcon] = React.useState<string | null>(null);
  const [copiedId, setCopiedId] = React.useState<string | null>(null);

  // Filter States
  const [selectedDateRange, setSelectedDateRange] = React.useState<string>('All Time');
  const [selectedType, setSelectedType] = React.useState<string>('All Types');
  const [selectedCountry, setSelectedCountry] = React.useState<string>('All Countries');
  const [selectedStatus, setSelectedStatus] = React.useState<string>('All Statuses');
  const [customStartDate, setCustomStartDate] = React.useState<Date | null>(null);
  const [customEndDate, setCustomEndDate] = React.useState<Date | null>(null);
  const [isDatePickerVisible, setIsDatePickerVisible] = React.useState<boolean>(false);

  // Selected transaction state for details panel
  const [selectedTransaction, setSelectedTransaction] = React.useState<Transaction | null>(null);
  const [isPanelVisible, setIsPanelVisible] = React.useState<boolean>(false);

  // Filter Options (Derived from Credit transactions)
  const typeOptions = React.useMemo(
    () => ['All Types', ...Array.from(new Set(transactions.map(tx => tx.type))).sort()],
    [transactions]
  );
  const countryOptions = React.useMemo(
    () => ['All Countries', ...Array.from(new Set(transactions.map(tx => tx.location || 'N/A'))).sort()],
    [transactions]
  );
  const statusOptions = React.useMemo(() => ['All Statuses', 'Completed', 'Pending', 'Failed', 'Scheduled'], []);
  const dateRangeOptions = ['All Time', 'Last 7 Days', 'Last 30 Days', 'Last 90 Days', 'Custom date'];

  // Filtering Logic
  const filteredTransactions = React.useMemo(() => {
      return transactions.filter(tx => {
          const txDate = new Date(tx.transactionDate);

          // Date Range Filter
          let dateMatch = true;
          if (selectedDateRange === 'Custom date') {
              if (customStartDate && customEndDate) {
                  const adjustedEndDate = new Date(customEndDate);
                  adjustedEndDate.setHours(23, 59, 59, 999);
                  dateMatch = txDate >= customStartDate && txDate <= adjustedEndDate;
              } else if (customStartDate) {
                  dateMatch = txDate >= customStartDate;
              } else {
                  dateMatch = true;
              }
          } else if (selectedDateRange === 'Last 7 Days') {
              const startDate = new Date(); startDate.setDate(startDate.getDate() - 7); dateMatch = txDate >= startDate;
          } else if (selectedDateRange === 'Last 30 Days') {
              const startDate = new Date(); startDate.setDate(startDate.getDate() - 30); dateMatch = txDate >= startDate;
          } else if (selectedDateRange === 'Last 90 Days') {
              const startDate = new Date(); startDate.setDate(startDate.getDate() - 90); dateMatch = txDate >= startDate;
          }

          // Type Filter
          const typeMatch = selectedType === 'All Types' || tx.type === selectedType;

          // Country Filter
          const countryMatch = selectedCountry === 'All Countries' || tx.location === selectedCountry;

          // Status Filter
          const statusMatch = selectedStatus === 'All Statuses' || tx.status === selectedStatus;

          // Search Term Filter
          const matchesSearch = searchTerm === '' ||
                                (tx.businessPartner && tx.businessPartner.toLowerCase().includes(searchTerm.toLowerCase())) ||
                                tx.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                tx.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                tx.amount.toString().includes(searchTerm) ||
                                tx.currency.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                (tx.location && tx.location.toLowerCase().includes(searchTerm.toLowerCase()));

          return dateMatch && typeMatch && countryMatch && statusMatch && matchesSearch;
      });
  }, [transactions, searchTerm, selectedDateRange, selectedType, selectedCountry, selectedStatus, customStartDate, customEndDate]);

  // Calculate current balance per country and currency
  const balanceByCountry = React.useMemo(() => {
      const countryBalances: { [key: string]: { [currency: string]: number } } = {};
      const previousCountryBalances: { [key: string]: { [currency: string]: number } } = {};
      
      filteredTransactions.forEach(tx => {
          const country = tx.location || 'Unknown';
          if (!countryBalances[country]) {
              countryBalances[country] = {};
              previousCountryBalances[country] = {};
          }
          
          if (!countryBalances[country][tx.currency]) {
              countryBalances[country][tx.currency] = 0;
              previousCountryBalances[country][tx.currency] = 0;
          }
          
          countryBalances[country][tx.currency] += tx.amount;
          
          // For demonstration, we'll calculate a "previous" balance by reducing current by 5-15%
          if (tx.status === 'Completed') {
              const changePercent = 0.05 + Math.random() * 0.1; // 5-15% variation
              previousCountryBalances[country][tx.currency] += tx.amount * (1 - changePercent);
          }
      });
      
      return { current: countryBalances, previous: previousCountryBalances };
  }, [filteredTransactions]);
  
  // Calculate total balance across all countries and currencies
  const totalBalance = React.useMemo(() => {
      let total = 0;
      let previousTotal = 0;
      
      // For simplicity, convert everything to USD at fixed rates
      const exchangeRates: { [currency: string]: number } = {
          'AED': 0.27, 'SAR': 0.27, 'QAR': 0.27, 'KWD': 3.27, 'BHD': 2.65, 'USD': 1
      };
      
      Object.values(balanceByCountry.current).forEach(currencies => {
          Object.entries(currencies).forEach(([currency, amount]) => {
              total += amount * (exchangeRates[currency] || 1);
          });
      });
      
      Object.values(balanceByCountry.previous).forEach(currencies => {
          Object.entries(currencies).forEach(([currency, amount]) => {
              previousTotal += amount * (exchangeRates[currency] || 1);
          });
      });
      
      return { current: total, previous: previousTotal, change: total - previousTotal };
  }, [balanceByCountry]);

  // Calculate loan metrics
  const loanMetrics = React.useMemo(() => {
    const loanDisbursements = filteredTransactions.filter(tx => tx.type === 'Loan Disbursement');
    const totalLoaned = loanDisbursements.reduce((sum, tx) => sum + tx.amount, 0);
    const avgInterestRate = loanDisbursements.reduce((sum, tx) => 
      sum + (tx.loanDetails?.interestRate || 0), 0) / (loanDisbursements.length || 1);
    
    const repayments = filteredTransactions.filter(tx => 
      tx.type === 'Principal Repayment' || tx.type === 'Early Repayment');
    const totalRepaid = repayments.reduce((sum, tx) => sum + tx.amount, 0);
    
    const interestPayments = filteredTransactions.filter(tx => tx.type === 'Interest Payment');
    const totalInterest = interestPayments.reduce((sum, tx) => sum + tx.amount, 0);
    
    return {
      totalLoaned,
      avgInterestRate,
      totalRepaid,
      totalInterest,
      activeLoans: loanDisbursements.length,
      repaymentRate: totalRepaid / (totalLoaned || 1) * 100
    };
  }, [filteredTransactions]);
  
  // Handler for custom date changes
   const handleCustomDateChange = (dates: [Date | null, Date | null]) => {
        const [start, end] = dates;
        setCustomStartDate(start);
        setCustomEndDate(end);
    };

   const handleDateRangeChange = (value: string) => {
        setSelectedDateRange(value);
        if (value === 'Custom date') {
            setIsDatePickerVisible(true);
        } else {
            setIsDatePickerVisible(false);
            setCustomStartDate(null);
            setCustomEndDate(null);
        }
    };

    // Handler for copying ID
    const handleCopyID = async (id: string) => {
      try {
        await navigator.clipboard.writeText(id);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 1500);
      } catch (err) {
        console.error('Failed to copy ID: ', err);
      }
    };

    // Handler for row click to open details panel
    const handleRowClick = (transaction: Transaction) => {
      setSelectedTransaction(transaction);
      setTimeout(() => setIsPanelVisible(true), 10);
    };

    // Handler to close the details panel
    const handleCloseDetailsPanel = () => {
      setIsPanelVisible(false);
      setTimeout(() => setSelectedTransaction(null), 200);
    };

  return (
    <MainLayout>
      <div style={{ padding: '24px' }}>
        {/* Back Button */}
        <button
            onClick={() => window.history.back()}
            style={{
                background: 'none',
                border: 'none',
                padding: 0,
                marginBottom: '18px',
                cursor: 'pointer',
                color: '#64748b',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                transition: 'color 0.2s ease'
            }}
        >
            <ArrowLeft size={16} style={{ marginRight: '6px' }} />
            Back to Wallet Dashboard
        </button>

        {/* Header Area */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ marginBottom: '20px' }}>
            <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 600, color: '#111827', lineHeight: '1.2' }}>Capital Wallet</h1>
            <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '14px' }}>View balances and transactions for the B2B Credit product.</p>
          </div>
        </div>

        {/* Filters Row */}
        <div style={{ padding: '16px 0', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '16px', marginBottom: '24px' }}>
            {/* Filters */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <DropdownFilter label="Date" options={dateRangeOptions} selectedValue={selectedDateRange} onChange={handleDateRangeChange} />
                {isDatePickerVisible && (
                    <div style={{ marginLeft: '-6px' }}>
                        <RangeDatePicker startDate={customStartDate} endDate={customEndDate} onChange={handleCustomDateChange} />
                    </div>
                )}
                <DropdownFilter label="Type" options={typeOptions} selectedValue={selectedType} onChange={setSelectedType} />
                <DropdownFilter label="Country" options={countryOptions} selectedValue={selectedCountry} onChange={setSelectedCountry} />
                <DropdownFilter label="Status" options={statusOptions} selectedValue={selectedStatus} onChange={setSelectedStatus} />
            </div>
            {/* Search */}
            <div style={{ marginLeft: 'auto' }}>
                <input
                  type="search"
                  placeholder="Search partner, type, amount..."
                  style={{
                      padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px',
                      minWidth: '200px'
                  }}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>
        </div>

        {/* Dashboard Grid */}
        <div style={{ marginBottom: '32px' }}>
          {/* Metrics Row */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: '16px',
            marginBottom: '64px'
          }}>
            <MainBalanceWidget 
              title="Total Balance" 
              totalBalance={loanMetrics.totalLoaned - loanMetrics.totalRepaid} 
              change={loanMetrics.totalLoaned * 0.05} 
              currency="USD" 
              icon={<DollarSign />}
            />

            <MetricWidget 
              title="Total Loans Disbursed"
              value={formatCurrency(loanMetrics.totalLoaned, 'USD')} 
              icon={<Send />} 
              secondaryValue={`${loanMetrics.activeLoans} active loans`}
              bgColor="#eef2ff"
              iconColor="#4f46e5"
            />
            
            <MetricWidget 
              title="Repayment Rate" 
              value="96.5%"
              icon={<Percent />} 
              secondaryValue={formatCurrency(loanMetrics.totalRepaid, 'USD')} 
              bgColor="#f0fdf4"
              iconColor="#10b981"
            />

            <MetricWidget 
              title="Interest Rate" 
              value={`${loanMetrics.avgInterestRate.toFixed(2)}%`} 
              icon={<BarChart2 />} 
              secondaryValue={formatCurrency(loanMetrics.totalInterest, 'USD') + " collected"} 
              bgColor="#fef3c7" 
              iconColor="#d97706" 
            />
            
            <MetricWidget 
              title="Business Partners" 
              value="15" 
              icon={<Briefcase />} 
              secondaryValue="4 new this quarter" 
              bgColor="#f3f4f6"
              iconColor="#6b7280"
            />
          </div>
        </div>

        {/* Transaction History Section */}
        <section style={{ backgroundColor: 'white', borderRadius: '8px', overflow: 'hidden' }}>
          {/* Table */}
          {filteredTransactions.length > 0 ? (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead style={{ backgroundColor: '#f9fafb' }}>
                <tr style={{ borderBottom: '1px solid #e5e7eb', textAlign: 'left', color: '#6b7280' }}>
                  <th style={{ fontWeight: 500, padding: '12px 16px', textAlign: 'center' }}>ID</th>
                  <th style={{ fontWeight: 500, padding: '12px 16px' }}>Created at</th>
                  <th style={{ fontWeight: 500, padding: '12px 16px' }}>Business Partner</th>
                  <th style={{ fontWeight: 500, padding: '12px 16px' }}>Country</th>
                  <th style={{ fontWeight: 500, padding: '12px 16px' }}>Category</th>
                  <th style={{ fontWeight: 500, padding: '12px 16px' }}>Type</th>
                  <th style={{ fontWeight: 500, padding: '12px 16px', textAlign: 'right' }}>Amount</th>
                  <th style={{ fontWeight: 500, padding: '12px 16px', textAlign: 'center' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map((tx, index) => (
                  <tr
                    key={tx.id}
                    data-transaction-row="true"
                    style={{
                      borderBottom: '1px solid #f3f4f6', cursor: 'pointer',
                      backgroundColor: hoveredRowIndex === index ? '#f9fafb' : 'transparent',
                      transition: 'background-color 0.1s ease-in-out', height: '35px'
                    }}
                    onMouseEnter={() => setHoveredRowIndex(index)}
                    onMouseLeave={() => setHoveredRowIndex(null)}
                    onClick={() => handleRowClick(tx)}
                  >
                    {/* ID Cell */}
                    <td style={{ padding: '7px 16px', width: '60px', textAlign: 'center' }}>
                       <div
                         style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', height: '100%', position: 'relative' }}
                         onClick={(e) => { e.stopPropagation(); handleCopyID(tx.id); }}
                         title={copiedId === tx.id ? 'ID copied' : `Click to copy ID: ${tx.id}`}
                         onMouseEnter={() => setHoveredIdIcon(tx.id)}
                         onMouseLeave={() => setHoveredIdIcon(null)}
                       >
                         {copiedId === tx.id ? <CheckIcon style={{ color: '#10b981' }} size={16} />
                           : hoveredIdIcon === tx.id ? <CopyIcon style={{ color: '#6b7280' }} size={14} />
                           : <HashIcon style={{ color: '#9ca3af' }} size={14} />}
                       </div>
                    </td>
                    {/* Created at Cell */}
                    <td style={{ padding: '7px 16px', color: '#374151', whiteSpace: 'nowrap', width: '140px' }}>
                        {`${formatDateTime(tx.transactionDate).date}, ${formatDateTime(tx.transactionDate).time}`}
                    </td>
                    {/* Business Partner Cell */}
                    <td style={{ padding: '7px 16px' }}>
                      <div style={{ fontWeight: 400, color: '#111827' }}>{tx.businessPartner}</div>
                    </td>
                    {/* Country Cell */}
                    <td style={{ padding: '7px 16px', color: '#374151', width: '80px' }}>{tx.location || 'N/A'}</td>
                    {/* Category Cell */}
                    <td style={{ padding: '7px 16px' }}>
                        <CategoryBadge category={getTransactionCategoryBase(tx)} />
                    </td>
                    {/* Type Cell */}
                    <td style={{ padding: '7px 16px', fontWeight: 400, color: '#111827' }}>{tx.type}</td>
                    {/* Amount Cell */}
                    <td style={{ padding: '7px 16px', fontWeight: 500, color: '#111827', textAlign: 'right' }}>
                      {formatCurrency(tx.amount, tx.currency)}
                    </td>
                    {/* Status Cell */}
                    <td style={{ padding: '7px 16px', textAlign: 'center' }}>
                      <StatusBadge status={tx.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
             // Empty State
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#6b7280', borderTop: '1px solid #e5e7eb', margin: '0 24px' }}>
               <FileText size={48} style={{ color: '#d1d5db', marginBottom: '16px' }} />
              <p style={{ fontSize: '16px', fontWeight: 500, color: '#111827', marginBottom: '4px' }}>No Credit Transactions Found</p>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>Try adjusting your filter criteria.</p>
            </div>
           )}
        </section>
      </div>

      {/* Render Transaction Details Panel */}
      {selectedTransaction && (
        <TransactionDetailsPanel
          transaction={selectedTransaction}
          onClose={handleCloseDetailsPanel}
          isVisible={isPanelVisible}
        />
      )}
    </MainLayout>
  );
};

export default CreditWalletPage;
